export type ThemeMode = 'auto' | 'light' | 'dark';
export type AppliedTheme = 'light' | 'dark';

const STORAGE_KEY = 'eo-theme-mode';
const VALID: ReadonlySet<ThemeMode> = new Set(['auto', 'light', 'dark']);

type Listener = (mode: ThemeMode) => void;

let currentMode: ThemeMode = 'auto';
const listeners = new Set<Listener>();

function readFromStorage(): ThemeMode {
  if (typeof window === 'undefined') return 'auto';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw && VALID.has(raw as ThemeMode) ? (raw as ThemeMode) : 'auto';
}

function prefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveAppliedMode(mode: ThemeMode): AppliedTheme {
  if (mode === 'auto') return prefersDark() ? 'dark' : 'light';
  return mode;
}

export function getAppliedMode(): AppliedTheme {
  return resolveAppliedMode(currentMode);
}

function applyToDom(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolveAppliedMode(mode) === 'dark');
}

export function getThemeMode(): ThemeMode {
  return currentMode;
}

export function setThemeMode(mode: ThemeMode): void {
  currentMode = mode;
  if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, mode);
  applyToDom(mode);
  listeners.forEach((fn) => fn(mode));
}

export function subscribeThemeMode(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// One-shot at boot: adopt the persisted preference and keep 'auto' tracking the
// OS without a reload. The pre-paint script in index.html has already applied
// the same resolution, so this never causes a flash.
export function initTheme(): void {
  currentMode = readFromStorage();
  applyToDom(currentMode);

  if (typeof window !== 'undefined' && window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (currentMode === 'auto') applyToDom('auto');
    });
  }
}
